// ============================================================================
// Centralised SEO helpers for Ekta Wire Machines.
// - Canonical domain is fixed to the preferred (non-www) production domain.
// - JSON-LD builders produce valid schema WITHOUT inventing data (only real,
//   already-present fields are used).
// - auditSeo() returns non-blocking warnings for the admin panel.
// ============================================================================

import { img } from "@/api";

// Preferred canonical domain (non-www), used everywhere for canonical + schema.
export const CANONICAL_ORIGIN = "https://ektawiremachines.com";

// Recommended lengths (Google-friendly). Used only for non-blocking warnings.
export const TITLE_MIN = 30;
export const TITLE_MAX = 60;
export const DESC_MIN = 70;
export const DESC_MAX = 160;

// Pages that should almost never be set to noindex (drives an admin warning).
export const IMPORTANT_PAGE_KEYS = ["home", "machines", "about", "contact", "products"];

// Normalise a router pathname: strip trailing slash (except root), drop query/hash.
export function cleanPath(pathname) {
  if (!pathname) return "/";
  let p = String(pathname).split("?")[0].split("#")[0];
  if (p.length > 1) p = p.replace(/\/+$/, "");
  return p || "/";
}

// Absolute URL on the canonical domain for an internal site path.
export function absUrl(path) {
  if (!path) return CANONICAL_ORIGIN;
  const s = String(path);
  if (/^https?:\/\//i.test(s)) return s;
  return `${CANONICAL_ORIGIN}${s.startsWith("/") ? "" : "/"}${cleanPath(s)}`;
}

function validHttp(u) {
  return typeof u === "string" && /^https?:\/\//i.test(u) && u !== "#";
}

// Organization schema built only from real site fields.
export function buildOrganizationLd(site) {
  const s = site || {};
  const defaults = s.seo_defaults || {};
  const name = defaults.site_name || s.brand_name || "Ekta Wire Machines Pvt. Ltd.";
  const org = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name,
    url: CANONICAL_ORIGIN,
  };
  const logo = s.logo_url ? img(s.logo_url) : "";
  if (logo) org.logo = logo;
  if (s.footer_about) org.description = s.footer_about;

  // sameAs — only genuine, configured social links.
  const sameAs = [];
  (s.social_links || []).forEach((l) => { if (validHttp(l && l.url)) sameAs.push(l.url); });
  const social = s.social || {};
  ["linkedin", "youtube", "instagram", "facebook"].forEach((k) => { if (validHttp(social[k])) sameAs.push(social[k]); });
  if (sameAs.length) org.sameAs = Array.from(new Set(sameAs));

  // contactPoint — only if a real phone exists.
  const phone = (s.header_phone && s.header_phone.number) || s.primary_phone || "";
  if (phone) {
    org.contactPoint = [{ "@type": "ContactPoint", telephone: phone, contactType: "sales" }];
  }
  return org;
}

// WebSite schema (no SearchAction — the site has no on-site search, so we don't invent one).
export function buildWebsiteLd(site) {
  const s = site || {};
  const defaults = s.seo_defaults || {};
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: defaults.site_name || s.brand_name || "Ekta Wire Machines Pvt. Ltd.",
    url: CANONICAL_ORIGIN,
  };
}

// BreadcrumbList schema from [{ name, path }]. Last item may omit path (current page).
export function buildBreadcrumbLd(items) {
  const list = (items || []).filter(Boolean);
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: list.map((it, i) => {
      const el = { "@type": "ListItem", position: i + 1, name: it.name };
      if (it.path) el.item = absUrl(it.path);
      return el;
    }),
  };
}

// Non-blocking SEO audit. Returns [{ level: 'warn'|'info', msg }].
export function auditSeo(opts) {
  const o = opts || {};
  const w = [];
  const title = (o.title || "").trim();
  const desc = (o.description || "").trim();

  if (!title) w.push({ level: "warn", msg: "Missing SEO title." });
  else {
    if (title.length < TITLE_MIN) w.push({ level: "warn", msg: `SEO title is short (${title.length} chars). Aim for ${TITLE_MIN}–${TITLE_MAX}.` });
    if (title.length > TITLE_MAX) w.push({ level: "warn", msg: `SEO title is long (${title.length} chars). Aim for ${TITLE_MIN}–${TITLE_MAX}.` });
  }
  if (o.duplicateTitle) w.push({ level: "warn", msg: "Duplicate SEO title — another page uses the same title." });

  if (!desc) w.push({ level: "warn", msg: "Missing meta description." });
  else {
    if (desc.length < DESC_MIN) w.push({ level: "warn", msg: `Meta description is short (${desc.length} chars). Aim for ${DESC_MIN}–${DESC_MAX}.` });
    if (desc.length > DESC_MAX) w.push({ level: "warn", msg: `Meta description is long (${desc.length} chars). Aim for ${DESC_MIN}–${DESC_MAX}.` });
  }

  if (o.hasCanonicalField && !(o.canonical || "").trim()) {
    w.push({ level: "info", msg: `No custom canonical set — will default to ${CANONICAL_ORIGIN}<page-path>.` });
  }
  if (o.hasH1Field && !(o.h1 || "").trim()) {
    w.push({ level: "warn", msg: "Missing/empty H1 heading for this page." });
  }
  if (typeof o.missingAltCount === "number" && o.missingAltCount > 0) {
    w.push({ level: "warn", msg: `${o.missingAltCount} image(s) missing ALT text.` });
  }
  if (o.robotsIndex === false && o.important) {
    w.push({ level: "warn", msg: "Noindex is enabled on an important page — this can remove it from Google." });
  }
  return w;
}
